<template>
  <div class="notifications">
    <b-dropdown
      id="notificationsMenu"
      ref="notificationsMenu"
      right
      no-caret
      @shown="dismissUnseen"
    >
      <template #button-content>
        <div
          @click="trackNotificationIconClicked"
          @keypress.space="trackNotificationIconClicked"
        >
          <span class="sr-only">
            Notifications Menu
          </span>
          <acv-icon
            icon="notifications_none"
          />
          <acv-badge
            v-if="unseenCount > 0"
            id="unseenBadge"
            class="notification-badge"
            :content="unseenCountDisplay"
          />
        </div>
      </template>
      <b-dropdown-header>
        <div class="row">
          <div class="col col-xs-6">
            <h6>Notifications</h6>
          </div>
          <div
            v-if="notifications.length"
            class="col col-xs-6 text-right"
          >
            <button
              type="button"
              @click="markAllRead"
            >
              Mark All <span class="hidden-xs">
                as Read
              </span>
            </button>
          </div>
        </div>
      </b-dropdown-header>
      <div class="items-wrapper">
        <acv-notifications-item
          v-for="notification in notifications"
          :id="notification.id"
          :key="notification.id"
          :body="notification.body"
          :created="notification.created_timestamp"
          :link="getAuctionLink(notification)"
          :thumbnail-url="notification.photo ? notification.photo.acv_thumbnail : ''"
          :title="notification.title"
          :read="notification.read"
          :go-to-auction-on-notification-click="goToAuctionOnNotificationClick"
          @notificationClicked="handleNotificationItemClicked"
        />
      </div>
      <b-dropdown-item
        v-if="notifications.length"
        :to="viewAllLink.to"
        class="view-all-btn"
        @click="trackViewAllClicked"
      >
        {{ viewAllLink.text }}
      </b-dropdown-item>
      <b-dropdown-item
        v-if="!notifications.length"
        :disabled="true"
        class="no-notifications"
      >
        <span>
          You currently have no notifications.
        </span>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { AcvBadge, AcvIcon } from '@acv/shared-vuejs';
import { BDropdown, BDropdownHeader, BDropdownItem } from 'bootstrap-vue';
import links from '@/constants/links';
import Analytics from '@/mixins/Analytics';
import WorkerHelper from '@/workers/WorkerHelper';

export default {
  name: 'AcvNotificationsMenu',
  components: {
    AcvNotificationsItem: () => import('@/components/Notifications/NotificationsItem.vue'),
    AcvBadge,
    BDropdown,
    BDropdownHeader,
    BDropdownItem,
    AcvIcon,
  },
  mixins: [
    Analytics,
  ],
  props: {
    icon: {
      type: [Array, String],
      default: 'bell',
    },
    showUnseenCount: {
      type: Boolean,
      default: true,
    },
    goToAuctionOnNotificationClick: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      links,
      worker: null,
    };
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'unseenCount',
      'isAuthorized',
      'isLimitedUser',
    ]),
    notifications() {
      return this.$store.state.notifications.notifications;
    },
    viewAllLink() {
      return {
        to: '/notifications',
        text: 'View All',
      };
    },
    unseenCountDisplay() {
      return this.showUnseenCount
        ? this.$store.getters.unseenCountDisplay
        : '';
    },
  },
  created() {
    this.worker = new WorkerHelper();
    this.worker.on('setInterval', () => {
      this.loadRecents();
    });

    this.loadRecents();
    if (this.worker) {
      this.worker.trigger('setInterval', { interval: 15000 });
    }
  },
  destroyed() {
    if (this.worker) {
      this.worker.terminate();
    }
  },
  methods: {
    handleNotificationItemClicked($event) {
      this.$refs.notificationsMenu.hide();
      this.$emit('notificationClicked', $event);
    },
    loadRecents() {
      if (this.isAuthorized && !this.isLimitedUser) {
        this.$store.dispatch('getRecent');
      }
    },
    trapMouseOver(e) {
      // the bs component has a mouseover event that auto-scrolls
      // items into place, we don't want that.
      e.preventDefault();
      e.stopPropagation();
    },
    getAuctionLink(notification) {
      return `/auction/${notification.auction_id}`;
    },
    toggleMenu(evt) {
      this.$refs.notificationsMenu.toggle(evt);
    },
    dismissUnseen() {
      // reset the unseen count when the menu is shown
      this.$store.dispatch('markAllSeen');
    },
    markAllRead() {
      if (this.$route.name === 'notifications') {
        // dispatch and let notification list component call api
        this.$root.$emit('markAllRead');
      } else {
        // call api to mark notifications read
        this.$store.dispatch('markAllRead');
        this.track(this.eventNames.notifications.markAllReadClicked);
      }
    },
    trackViewAllClicked() {
      this.track(this.eventNames.notifications.viewAllClicked);
    },
    trackNotificationIconClicked() {
      this.track(this.eventNames.notifications.iconClicked);
    },
  },
};
</script>

<style lang="scss" scoped>
.notifications {
  position: relative;

  .btn-group {
    top: -1px;

    .btn-secondary {
      background: none;
      border: none;
    }
  }

  .notification-badge {
    z-index: 5;
    padding-top: 2px;
    padding-bottom: 2px;
    right: -10px;
    top: -5px;

    ::v-deep .acv-badge__content {
      font-size: 12px;
    }
  }

  ::v-deep #notificationsMenu {
    .btn {
      line-height: 1;
      border: 0;

      svg {
        color: rgb(255 255 255 / 80%);
        font-size: 22px;
      }

      &:hover {
        background: constants.$darker-gray;
        border: 0;

        i,
        svg {
          color: white;
        }
      }

      &:focus,
      &:active {
        outline: 0;
        border: 0;
        box-shadow: none;
      }
    }

    .dropdown-toggle {
      background-color: transparent;
    }

    .dropdown-menu {
      padding-top: 0;
      padding-bottom: 0;
      margin-top: 18px;
      box-shadow: 0 3px 6px rgb(0 0 0 / 16%), 0 3px 6px rgb(0 0 0 / 23%);
      border: 0 !important;
      transform: translateY(-2px);

      @media screen and (min-width: constants.$sm-min) {
        width: 375px;
      }

      .items-wrapper {
        max-height: 460px;
        overflow-y: auto;
      }

      .view-all-btn {
        a {
          &.dropdown-item {
            line-height: 15px;
            width: 100% !important;
            text-align: center;
            border: none;
            background: color.adjust(#ededed, $blackness: 5%);
            font-size: 13px;
            padding: 12px;
            color: rgb(0 0 0 / 67%);
            border-top: solid 1px rgb(0 0 0 / 10%);
            font-weight: normal;
            outline: none;
            display: block;

            &:hover {
              background: color.adjust(#ededed, $blackness: 10%);
            }

            &:focus {
              outline: none !important;
            }
          }
        }
      }

      .no-notifications {
        cursor: default;
        background: white;
        font-size: 14px;
        width: 375px;
        text-align: center;
        padding-top: 15px;
        height: 100px;

        a {
          cursor: default;
        }

        span {
          cursor: default;
        }
      }
    }
  }
}

.dropdown-header {
  cursor: default;
  background: white;
  border-top: solid 4px constants.$orange;
  text-align: left;
  padding: 5px 15px;
  border-bottom: solid 1px rgb(0 0 0 / 10%);

  .col {
    line-height: 1.2;

    h6 {
      padding-top: 10px;
      font-size: 13px;
      font-weight: bold;
      color: rgb(0 0 0 / 67%);
    }

    button {
      border: none;
      background: none;
      font-size: 13px;
      padding: 10px 0;
      color: rgb(0 0 0 / 40%);
      outline: none;

      &:hover {
        color: constants.$orange;
      }
    }
  }
}
</style>
